/**
 * Presentation helpers for the RCC Buffers page.
 *
 * Pure functions only. Buffer status is never re-derived here: each card's BUFFER metric
 * carries the server-computed `comparisonStatus`, and colour maps straight through the shared
 * dashboard helpers, as on the flow map.
 */

import { MetricName } from '../dashboard/constants';
import { MetricComparisonData } from '../dashboard/model';
import { ProcessPathCard } from '../flowMap/model';
import { findActual, findMetric, formatFlowNumber, processPathLabel } from '../flowMap/utils';
import { BufferState } from '../flowMap/constants';
import { ContainerStatus, ContainerType, TrendWindow } from './constants';
import { ContainerDetail, GetBuffersDetailsDataOutput } from './model';

const HOURS_PER_DAY = 24;

/** The card's BUFFER metric, or undefined when the service sent none. */
export const bufferMetricOf = (card: ProcessPathCard | undefined): MetricComparisonData | undefined =>
    findMetric(card, MetricName.BUFFER);

/** An actual-only reading on the card (e.g. AVG_DWELL_HOURS), or undefined. */
export const actualOf = (card: ProcessPathCard | undefined, metricName: MetricName): number | undefined =>
    findActual(card, metricName)?.actualValue;

/** The card's display name. */
export const bufferCardLabel = (card: ProcessPathCard | undefined): string =>
    processPathLabel(card?.topologyNode?.processPath);

/** A dwell in days to one decimal, e.g. 60 → "2.5 days"; an em dash when absent. */
export const formatDwellDays = (hours: number | null | undefined): string =>
    hours == null || Number.isNaN(hours) ? '—' : `${(hours / HOURS_PER_DAY).toFixed(1)} days`;

/** A container dwell: hours under a day ("6.0 hrs"), days from there ("6.4 days"). */
export const formatDwell = (hours: number | null | undefined): string => {
    if (hours == null || Number.isNaN(hours)) return '—';
    return hours < HOURS_PER_DAY ? `${hours.toFixed(1)} hrs` : formatDwellDays(hours);
};

/** Whole-percent capacity used, e.g. 90 → "90%". */
export const formatCapacityPercent = (percent: number | null | undefined): string =>
    percent == null || Number.isNaN(percent) ? '—' : `${Math.round(percent)}%`;

/**
 * A summary tile's change since last shift, for Meridian's TrendText: the text ("+120 vs last
 * shift"), the arrow, and the tone.
 *
 * Every tile counts something that should shrink — units, totes and pallets sitting in a
 * buffer, units potentially lost — so a rise is the bad direction (negative) and a fall the good
 * one (positive). No change has no arrow and a neutral tone.
 */
export const describeDelta = (
    delta: number | null | undefined
): { text: string; arrow?: 'up' | 'down'; tone: 'negative' | 'positive' | 'neutral' } | undefined => {
    if (delta == null || Number.isNaN(delta)) return undefined;
    // `|| 0` folds -0 (e.g. Math.round(-0.3)) into 0.
    const rounded = Math.round(delta) || 0;
    if (rounded > 0) {
        return { text: `+${formatFlowNumber(rounded)} vs last shift`, arrow: 'up', tone: 'negative' };
    }
    if (rounded < 0) {
        return { text: `${formatFlowNumber(rounded)} vs last shift`, arrow: 'down', tone: 'positive' };
    }
    return { text: '0 vs last shift', tone: 'neutral' };
};

/** Row labels for the container types. */
export const CONTAINER_TYPE_LABELS: Record<string, string> = {
    [ContainerType.PALLET]: 'Pallet',
    [ContainerType.TOTE]: 'Tote',
    [ContainerType.UNIT]: 'Unit',
};

/** The drill-down header's container counts, in display order; each is a link that shows its type in the table. */
export const CONTAINER_TYPE_FILTERS: {
    value: ContainerType;
    label: string;
    countKey: keyof GetBuffersDetailsDataOutput;
}[] = [
    { value: ContainerType.PALLET, label: 'Pallets', countKey: 'pallets' },
    { value: ContainerType.TOTE, label: 'Totes', countKey: 'totes' },
    { value: ContainerType.UNIT, label: 'Units', countKey: 'units' },
];

/** Row labels for the container statuses, as named in the status index under the table. */
export const CONTAINER_STATUS_LABELS: Record<string, string> = {
    [ContainerStatus.DROPZONE]: 'Dropzone',
    [ContainerStatus.PROCESSING]: 'Processing',
    [ContainerStatus.POTENTIAL_LOST]: 'Potential Lost',
};

/** The trend-window toggle, in display order. */
export const TREND_WINDOW_OPTIONS: { value: TrendWindow; label: string }[] = [
    { value: TrendWindow.SHIFT, label: 'Shift' },
    { value: TrendWindow.TWENTY_FOUR_HOUR, label: '24hr' },
    { value: TrendWindow.THREE_DAY, label: '3 Days' },
    { value: TrendWindow.WEEK, label: '1 Week' },
];

/** The trend window the drill-down opens on. */
export const DEFAULT_TREND_WINDOW = TrendWindow.SHIFT;

/** The dwell buckets of the drill-down header, in display order. */
export const DWELL_BUCKETS: { label: string; key: keyof GetBuffersDetailsDataOutput }[] = [
    { label: '0-12h', key: 'dwell0to12h' },
    { label: '12-24h', key: 'dwell12to24h' },
    { label: '1-3d', key: 'dwell1to3d' },
    { label: '3-7d', key: 'dwell3to7d' },
    { label: '7d+', key: 'dwell7dPlus' },
];

/** What each container status means, for the status index under the table. */
export const STATUS_INDEX_TEXT =
    'Dropzone = Palletized in Buffer Zone · Potential Lost = Missing Scan for 7+ days or Not Associated ' +
    'with Pallet for 3+ days · Processing = Waiting to be Processed, Dwelling < 3 days';

/** Avg dwell, in days, from which a card's dwell reads amber, and red (as on the prototype). */
export const DWELL_WARNING_DAYS = 1;
export const DWELL_CRITICAL_DAYS = 2;

/**
 * Meridian `Text` colour for an average dwell: green under a day, amber from a day, red from two.
 * Presentation only — the thresholds colour the figure; they grade nothing.
 */
export const dwellTextColor = (hours: number | null | undefined): 'success' | 'warning' | 'error' | 'secondary' => {
    if (hours == null || Number.isNaN(hours)) return 'secondary';
    const days = hours / HOURS_PER_DAY;
    if (days >= DWELL_CRITICAL_DAYS) return 'error';
    if (days >= DWELL_WARNING_DAYS) return 'warning';
    return 'success';
};

/** The key a card's trend is filed under in `bufferTrends`. */
export const trendKey = (processId: string | undefined, functionId: string | undefined): string =>
    `${processId ?? ''}:${functionId ?? ''}`;

/** Sparkline colours by buffer trend (Meridian status colours): red rising, green falling, grey flat. */
export const SPARKLINE_COLORS: Record<string, string> = {
    [BufferState.ACCUMULATING]: '#cc0c39',
    [BufferState.DEPLETING]: '#067d62',
    [BufferState.STABLE]: '#6c7778',
};

/** The sparkline's time-axis caption for each window. */
export const SPARKLINE_TIME_LABELS: Record<string, string> = {
    [TrendWindow.SHIFT]: 'Time (hours into shift)',
    [TrendWindow.TWENTY_FOUR_HOUR]: 'Time (last 24 hours)',
    [TrendWindow.THREE_DAY]: 'Time (last 3 days)',
    [TrendWindow.WEEK]: 'Time (last week)',
};

/** Containers sorted by dwell; rows without a dwell go last either way. Does not mutate. */
export const sortByDwell = (
    containers: ContainerDetail[],
    direction: 'ascending' | 'descending'
): ContainerDetail[] => {
    const sign = direction === 'ascending' ? 1 : -1;
    return containers
        .map((container, index) => ({ container, index }))
        .sort((a, b) => {
            const left = a.container.dwellHours;
            const right = b.container.dwellHours;
            if (left == null || right == null) {
                return (left == null ? 1 : 0) - (right == null ? 1 : 0) || a.index - b.index;
            }
            return sign * (left - right) || a.index - b.index;
        })
        .map(({ container }) => container);
};

const timeFormatter = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' });
const dateFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const hourFormatter = new Intl.DateTimeFormat('en-US', { hour: 'numeric' });

/** A last-scan time: "06:42 AM" when it is on the same day as `nowMs`, else "Oct 9, 06:42 AM". */
export const formatScanTime = (epochMs: number | null | undefined, nowMs: number = Date.now()): string => {
    if (epochMs == null || Number.isNaN(epochMs)) return '—';
    const time = timeFormatter.format(epochMs);
    return new Date(epochMs).toDateString() === new Date(nowMs).toDateString()
        ? time
        : `${dateFormatter.format(epochMs)}, ${time}`;
};

/** A trend-chart axis tick: the hour within a day, the date and hour over days, the date over a week. */
export const formatTrendTick = (epochMs: number, window: TrendWindow): string => {
    if (window === TrendWindow.WEEK) return dateFormatter.format(epochMs);
    if (window === TrendWindow.THREE_DAY) return `${dateFormatter.format(epochMs)} ${hourFormatter.format(epochMs)}`;
    return hourFormatter.format(epochMs);
};
